import { NavigateOptions, useNavigate, withRouteId } from '@melio/ar-domain';
import { useToast } from '@melio/penny';
import { FundingSource } from '@melio/platform-api';
import { forwardRef } from '@melio/platform-utils';
import { Route, Routes } from 'react-router-dom';
import { RecoilRoot } from 'recoil';

import {
  AddCardFundingSourceActivity,
  AddFundingSourceActivity,
  AddPlaidFundingSourceActivity,
  LoadFundingSourcesActivity,
  PaymentScheduledActivity,
  ReviewAndConfirmActivity,
} from '../activities';
import { usePaymentBuilder } from './usePaymentBuilder';
import { useTerribleHacks } from './useTerribleHacks';

enum AppRoutes {
  Root = './',
  AddFundingSource = 'add-payment-method',
  EditFundingSource = 'edit-payment-method',
  AddCard = 'add-card-payment-method',
  AddPlaid = 'add-plaid-payment-method',
  ReviewAndConfirm = 'review-and-confirm',
  Success = 'success/:id',
}

type SuccessPathParams = {
  id: string;
};

export type PaymentFlowProps = {
  onDone?: (id: string) => unknown;
  onBack?: VoidFunction;
  onClose?: VoidFunction;
  onError?: ErrorFunction;
};

export type GuestPaymentFlowProps = PaymentFlowProps & { billId: string };

export const GuestPaymentFlow = forwardRef<GuestPaymentFlowProps>((props, ref) => (
  <RecoilRoot>
    <PaymentFlowInner {...props} ref={ref} />
  </RecoilRoot>
));

GuestPaymentFlow.displayName = 'GuestPaymentFlow';

const PaymentFlowInner = forwardRef<GuestPaymentFlowProps>(
  ({ billId, onDone, onBack: externalOnBack, onError: _onError, onClose, ...props }, ref) => {
    // FIXME: This is a work around missing one last thing and delivery method
    useTerribleHacks(billId);

    const navigate = useNavigate<AppRoutes>({ withSearchparams: true });

    const goToReviewAndConfirm = (options?: NavigateOptions<never>) =>
      navigate(AppRoutes.ReviewAndConfirm, { withSearchparams: false, ...options });
    const goToMain = (options?: NavigateOptions) => navigate<never>(AppRoutes.Root, { historyBack: true, ...options });
    const goToAddFundingSource = () => navigate(AppRoutes.AddFundingSource);
    const goToEditFundingSource = () => navigate(AppRoutes.EditFundingSource);
    const addFundingSourceByType = (type: FundingSource['type']) => {
      switch (type) {
        case 'card':
          return navigate(AppRoutes.AddCard);
        case 'bank-account':
          return navigate(AppRoutes.AddPlaid);
      }
    };
    const goToSuccess = (id: string) => {
      navigate<SuccessPathParams>(AppRoutes.Success, { pathParams: { id } });
    };

    const { data: paymentDraft, ...paymentBuilder } = usePaymentBuilder({ billId });

    const toast = useToast();

    const onError = (error: PlatformError) => {
      toast({ type: 'error', title: error.message });
      _onError?.(error);
    };

    const setFundingSource = (id: string) =>
      paymentBuilder.setFundingSourceId(id).then(() => goToReviewAndConfirm({ replace: true }));

    const onConfirm = () => {
      paymentBuilder.createPayment().then(goToSuccess).catch(onError);
    };

    const SelectFundingSource = forwardRef<{ onBack?: VoidFunction }>((props, ref) => (
      <AddFundingSourceActivity
        billId={billId}
        onDone={addFundingSourceByType}
        onClose={onClose}
        {...props}
        ref={ref}
      />
    ));

    return (
      <RecoilRoot>
        <Routes>
          <Route
            index
            element={
              <LoadFundingSourcesActivity
                onError={onError}
                onDone={(id) => {
                  if (id) {
                    void setFundingSource(id);
                  } else {
                    goToAddFundingSource();
                  }
                }}
              />
            }
          />
          <Route
            path={AppRoutes.AddFundingSource}
            element={<SelectFundingSource onBack={externalOnBack} {...props} ref={ref} />}
          />
          <Route
            path={AppRoutes.EditFundingSource}
            element={<SelectFundingSource onBack={goToReviewAndConfirm} {...props} ref={ref} />}
          />
          <Route
            path={AppRoutes.AddCard}
            element={
              <AddCardFundingSourceActivity
                billId={billId}
                onDone={setFundingSource}
                onBack={goToMain}
                onError={onError}
                onClose={onClose}
              />
            }
          />
          <Route
            path={AppRoutes.AddPlaid}
            element={
              <AddPlaidFundingSourceActivity
                billId={billId}
                onDone={setFundingSource}
                onError={onError}
                onClose={goToAddFundingSource}
              />
            }
          />
          <Route
            path={AppRoutes.ReviewAndConfirm}
            element={
              <ReviewAndConfirmActivity
                onBack={() => goToMain({ replace: true })}
                billId={billId}
                paymentDraft={paymentDraft}
                onDone={onConfirm}
                onEditFundingSource={goToEditFundingSource}
                isSaving={paymentBuilder.isCreating}
              />
            }
          />
          <Route path={AppRoutes.Success} element={<PaymentScheduled />} />
        </Routes>
      </RecoilRoot>
    );
  }
);

const PaymentScheduled = withRouteId(PaymentScheduledActivity, 'paymentId');
